import { Fragment, useEffect, useRef, useState } from 'react'
import { Dimensions, Modal, Pressable, View } from 'react-native'
import { Divider, Icon, Portal, Text } from 'react-native-paper'
import { MenuProps } from './menu'
import { useTheme } from '../../../hooks/theme.hook'

export function MenuDropdown({
  visible,
  onDismiss,
  anchor,
  sections,
  anchorPosition = 'bottom',
}: MenuProps) {
  const theme = useTheme()
  const anchorRef = useRef<View>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const windowDimensions = Dimensions.get('window')

  useEffect(() => {
    if (!visible || !anchorRef.current) {
      return
    }

    anchorRef.current.measure((x, y, width, height, pageX, pageY) => {
      const menuWidth = 200
      const menuItemHeight = 48
      const estimatedMenuHeight = sections.reduce((acc, section) => {
        return (
          acc +
          (section.label ? 40 : 0) +
          section.items.length * menuItemHeight +
          (section.items.length > 0 ? 8 : 0)
        )
      }, 0)

      let top = pageY
      let left = pageX

      if (anchorPosition === 'bottom') {
        top = pageY + height + 4
      } else {
        top = pageY - estimatedMenuHeight - 4
      }

      // Adjust if menu goes off screen
      if (top + estimatedMenuHeight > windowDimensions.height) {
        top = pageY - estimatedMenuHeight - 4
      }
      if (top < 0) {
        top = 4
      }
      if (left + menuWidth > windowDimensions.width) {
        left = windowDimensions.width - menuWidth - 4
      }
      if (left < 0) {
        left = 4
      }

      setMenuPosition({ top, left })
    })
  }, [visible, sections, anchorPosition, windowDimensions])

  const menuStyles = {
    overlay: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    menuContainer: {
      position: 'absolute' as const,
      top: menuPosition.top,
      left: menuPosition.left,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.roundness * 2,
      minWidth: 200,
      maxWidth: windowDimensions.width - 8,
      elevation: 8,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 8,
      paddingVertical: theme.spacing(1),
    },
    menuItem: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      paddingHorizontal: theme.spacing(3),
      paddingVertical: theme.spacing(2),
      minHeight: 48,
    },
    menuItemText: {
      marginLeft: theme.spacing(2),
      fontSize: 16,
      color: theme.colors.onSurface,
    },
    menuSectionLabel: {
      paddingHorizontal: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      color: theme.colors.onSurfaceVariant,
    },
  }

  const handleItemPress = (onPress: () => void) => {
    onPress()
    onDismiss()
  }

  return (
    <>
      <View ref={anchorRef} collapsable={false}>
        {anchor}
      </View>
      <Portal>
        <Modal
          visible={visible}
          transparent
          animationType="fade"
          onRequestClose={onDismiss}
        >
          <Pressable style={menuStyles.overlay} onPress={onDismiss}>
            <Pressable
              style={menuStyles.menuContainer}
              onPress={(e) => e.stopPropagation()}
            >
              {sections.map((section, sectionIndex) => (
                <Fragment key={sectionIndex}>
                  {section.label && (
                    <Text
                      variant="labelSmall"
                      style={menuStyles.menuSectionLabel}
                    >
                      {section.label}
                    </Text>
                  )}
                  {section.items.map((item, itemIndex) => {
                    if (item.render) {
                      return (
                        <Fragment key={itemIndex}>{item.render()}</Fragment>
                      )
                    }

                    return (
                      <Pressable
                        key={itemIndex}
                        style={[
                          menuStyles.menuItem,
                          item.disabled && { opacity: 0.5 },
                        ]}
                        onPress={() =>
                          !item.disabled && handleItemPress(item.onPress)
                        }
                        disabled={item.disabled}
                      >
                        {item.icon && (
                          <>
                            {typeof item.icon === 'string' ? (
                              <Icon source={item.icon} size={24} />
                            ) : (
                              <View>{item.icon}</View>
                            )}
                          </>
                        )}
                        <Text style={menuStyles.menuItemText}>
                          {item.title}
                        </Text>
                      </Pressable>
                    )
                  })}
                  {sectionIndex < sections.length - 1 && <Divider />}
                </Fragment>
              ))}
            </Pressable>
          </Pressable>
        </Modal>
      </Portal>
    </>
  )
}
